import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { useSession } from '@/hooks/authHooks';
import {
  createSavedSearch,
  type CreateSavedSearchPayload,
  deleteSavedSearch,
  fetchSavedSearches,
  type SavedSearch,
  updateSavedSearch,
  type UpdateSavedSearchPayload,
} from '@/lib/api/savedSearches';
import { ArticleSource } from '@/schema';

export const SAVED_SEARCHES_QUERY_KEY = ['saved-searches'] as const;
const LOCAL_SAVED_SEARCHES_KEY = 'solomonq_saved_searches';

type SavedSearchQueryKey = readonly [
  ...typeof SAVED_SEARCHES_QUERY_KEY,
  'authenticated' | 'guest',
];

function getSavedSearchesQueryKey(
  isAuthenticated: boolean
): SavedSearchQueryKey {
  return [
    ...SAVED_SEARCHES_QUERY_KEY,
    isAuthenticated ? 'authenticated' : 'guest',
  ] as const;
}

function isArticleSource(value: unknown): value is ArticleSource {
  return (
    typeof value === 'string' &&
    (Object.values(ArticleSource) as string[]).includes(value)
  );
}

function canUseLocalStorage() {
  return (
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
  );
}

function parseLocalSavedSearch(raw: unknown): SavedSearch | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const { id, name, symbols, sources, hasSymbolOnly } =
    raw as Partial<SavedSearch> & {
      symbols?: unknown;
      sources?: unknown;
      hasSymbolOnly?: unknown;
    };

  if (typeof id !== 'number' || typeof name !== 'string') {
    return null;
  }

  const parsedSymbols = Array.isArray(symbols)
    ? symbols.filter((symbol): symbol is string => typeof symbol === 'string')
    : [];

  const parsedSources = Array.isArray(sources)
    ? sources.filter((source): source is ArticleSource =>
        isArticleSource(source)
      )
    : [];

  return {
    id,
    name,
    symbols: parsedSymbols,
    sources: parsedSources,
    hasSymbolOnly: Boolean(hasSymbolOnly),
  };
}

function readLocalSavedSearches(): SavedSearch[] {
  if (!canUseLocalStorage()) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(LOCAL_SAVED_SEARCHES_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((item) => parseLocalSavedSearch(item))
      .filter((search): search is SavedSearch => Boolean(search));
  } catch (error) {
    console.warn('Failed to read saved searches from local storage', error);
    return [];
  }
}

function persistLocalSavedSearches(searches: SavedSearch[]) {
  if (!canUseLocalStorage()) {
    return searches;
  }

  try {
    window.localStorage.setItem(
      LOCAL_SAVED_SEARCHES_KEY,
      JSON.stringify(searches)
    );
  } catch (error) {
    console.warn('Failed to persist saved searches to local storage', error);
  }

  return searches;
}

function createLocalSavedSearch(
  payload: CreateSavedSearchPayload
): SavedSearch {
  const currentSearches = readLocalSavedSearches();
  const newSearch: SavedSearch = {
    id: Date.now(),
    name: payload.name,
    symbols: [...payload.filters.symbols],
    sources: [...payload.filters.sources],
    hasSymbolOnly: Boolean(payload.filters.hasSymbolOnly),
  };

  const updatedSearches = [
    newSearch,
    ...currentSearches.filter((search) => search.id !== newSearch.id),
  ];

  persistLocalSavedSearches(updatedSearches);

  return newSearch;
}

function updateLocalSavedSearch(
  payload: UpdateSavedSearchPayload
): SavedSearch {
  const currentSearches = readLocalSavedSearches();
  const existingIndex = currentSearches.findIndex(
    (search) => search.id === payload.id
  );

  if (existingIndex === -1) {
    throw new Error('Saved search not found');
  }

  const updatedSearch: SavedSearch = {
    id: payload.id,
    name: payload.name,
    symbols: [...payload.filters.symbols],
    sources: [...payload.filters.sources],
    hasSymbolOnly: Boolean(payload.filters.hasSymbolOnly),
  };

  currentSearches[existingIndex] = updatedSearch;
  persistLocalSavedSearches(currentSearches);

  return updatedSearch;
}

function deleteLocalSavedSearch(id: number) {
  const currentSearches = readLocalSavedSearches();
  const remaining = currentSearches.filter((search) => search.id !== id);

  persistLocalSavedSearches(remaining);
}

export interface UseSavedSearchesOptions {
  enabled?: boolean;
  staleTime?: number;
}

export function useSavedSearches({
  enabled = true,
  staleTime,
}: UseSavedSearchesOptions = {}) {
  const { session, user, isPending } = useSession();
  const isAuthenticated = Boolean(session ?? user);
  const savedSearchQueryKey = useMemo(
    () => getSavedSearchesQueryKey(isAuthenticated),
    [isAuthenticated]
  );

  return useQuery({
    queryKey: savedSearchQueryKey,
    queryFn: ({ signal }) =>
      isAuthenticated
        ? fetchSavedSearches({ signal })
        : Promise.resolve(readLocalSavedSearches()),
    enabled: enabled && !isPending,
    staleTime,
    placeholderData: (previousData) => previousData,
  });
}

type CreateSavedSearchOptions = Omit<
  UseMutationOptions<SavedSearch, Error, CreateSavedSearchPayload>,
  'mutationFn'
>;

export function useCreateSavedSearchMutation(
  options?: CreateSavedSearchOptions
) {
  const { session, user, isPending } = useSession();
  const isAuthenticated = Boolean(session ?? user);
  const savedSearchQueryKey = useMemo(
    () => getSavedSearchesQueryKey(isAuthenticated),
    [isAuthenticated]
  );
  const shouldUseLocalStorage = !isAuthenticated && !isPending;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) =>
      shouldUseLocalStorage
        ? createLocalSavedSearch(payload)
        : createSavedSearch(payload),
    ...options,
    onSuccess: (newSearch, variables, _onMutateResult, context) => {
      queryClient.setQueryData<SavedSearch[]>(
        savedSearchQueryKey,
        (previous = []) => {
          const withoutCurrent = previous.filter(
            (search) => search.id !== newSearch.id
          );
          return [newSearch, ...withoutCurrent];
        }
      );
      options?.onSuccess?.(newSearch, variables, _onMutateResult, context);
    },
  });
}

type UpdateSavedSearchOptions = Omit<
  UseMutationOptions<SavedSearch, Error, UpdateSavedSearchPayload>,
  'mutationFn'
>;

export function useUpdateSavedSearchMutation(
  options?: UpdateSavedSearchOptions
) {
  const { session, user, isPending } = useSession();
  const isAuthenticated = Boolean(session ?? user);
  const savedSearchQueryKey = useMemo(
    () => getSavedSearchesQueryKey(isAuthenticated),
    [isAuthenticated]
  );
  const shouldUseLocalStorage = !isAuthenticated && !isPending;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) =>
      shouldUseLocalStorage
        ? updateLocalSavedSearch(payload)
        : updateSavedSearch(payload),
    ...options,
    onSuccess: (updatedSearch, variables, onMutateResult, context) => {
      queryClient.setQueryData<SavedSearch[]>(
        savedSearchQueryKey,
        (previous = []) =>
          previous.map((search) =>
            search.id === updatedSearch.id ? updatedSearch : search
          )
      );
      options?.onSuccess?.(updatedSearch, variables, onMutateResult, context);
    },
  });
}

type DeleteSavedSearchOptions = Omit<
  UseMutationOptions<void, Error, number>,
  'mutationFn'
>;

export function useDeleteSavedSearchMutation(
  options?: DeleteSavedSearchOptions
) {
  const { session, user, isPending } = useSession();
  const isAuthenticated = Boolean(session ?? user);
  const savedSearchQueryKey = useMemo(
    () => getSavedSearchesQueryKey(isAuthenticated),
    [isAuthenticated]
  );
  const shouldUseLocalStorage = !isAuthenticated && !isPending;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      if (shouldUseLocalStorage) {
        deleteLocalSavedSearch(id);
        return;
      }
      await deleteSavedSearch(id);
    },
    ...options,
    onSuccess: (data, id, onMutateResult, context) => {
      queryClient.setQueryData<SavedSearch[]>(
        savedSearchQueryKey,
        (previous = []) => previous.filter((search) => search.id !== id)
      );
      options?.onSuccess?.(data, id, onMutateResult, context);
    },
  });
}
